import React, { useEffect, useState, useRef } from 'react';
import { notification, Modal, Popover, Button, Skeleton } from 'antd';
import Select from 'components/Atom/Select';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { PageHeader } from 'components/Atom/PageHeader';
import { ResetButton, FilterButton } from 'components/Atom/Button';
import { ReloadOutlined, DiffOutlined } from '@ant-design/icons';
import Filter from './assets/filter';
import SearchField from 'components/Atom/SearchField';
import { getAllPricingModels, getSinglePricingModel, deletePricingModel } from 'api/subscriptions';
import { CreatePricingModel } from './createPricingModel';
import { handleDateRangeFormat, handleStateByPassData, checkUserPermissions } from 'utils/services';
import { formatInTimeZone } from 'date-fns-tz';
import { TIMEZONES, USER_ROLES } from 'utils/constant';
import { useNavigate, useLocation } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';

export const PricingModel = () => {
  const store = useSelector((state) => state?.company?.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  const [deletePricingModal, setDeletePricingModal] = useState(false);
  const [pricingModelData, setPricingModelData] = useState({});
  const [isPricingModelDeleting, setIsPricingModelDeleting] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState();
  const [filterToggle, setFilterToggle] = useState(false);
  const [isFilterModelOpen, setIsFilterModelOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [delayTimer, setDelayTimer] = useState();
  const [recordId, setRecordId] = useState('');
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;

  const [search, setSearch] = useState('');
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 20,
    search: '',
    currentPage: 1,
  });
  const [trigerApi, setTrigerApi] = useState(true);
  const handleDeletePricingModel = async (pricingModel, action = 'cancel') => {
    setIsPricingModelDeleting(true);
    const payload = { subscription: { next_action: action } };
    const response = await deletePricingModel(store?.isSelected ? store?._id : pricingModel?.store_id, pricingModel?.id, payload);
    if (response?.data?.success) {
      setDeletePricingModal(false);
      refetch();
    }
    setIsPricingModelDeleting(false);
  };

  const handleOpen = () => {
    setOpen(!open);
    setIsEdit(false);
  };

  const handleCreatePricingModel = () => {
    if (userStores?.length > 1 && !store?.isSelected) {
      notification.error({ message: 'Select store from the drop-down menu above', placement: 'bottomRight' });
      return;
    }
    setOpen(true);
    setIsEdit(false);
  };

  const fetchPricingModelData = async (pricingModel) => {
    setDataLoading(true);
    const response = await getSinglePricingModel(store?.isSelected ? store?._id : pricingModel?.store_id, pricingModel?.id);
    const data = response?.data?.data;
    setPricingModelData(data);
    setDataLoading(false);
  };

  const columnsData = [
    {
      title: 'Pricing Model ID',
      render: (value) => (
        <span style={{ cursor: 'pointer' }}>
          <ShortenId value={value?.id} />
        </span>
      ),
      key: 'id',
      fixed: 'left',
    },
    {
      title: 'Name',
      render: (value) => value?.name,
      key: 'name',
    },
    {
      title: 'Model Type',
      render: (value) => value?.type?.toUpperCase() || '',
      key: 'type',
    },
    {
      title: 'Charge Type',
      render: (value) => value?.charge_type?.toUpperCase() || '',
      key: 'charge_type',
    },
    {
      title: 'Time',
      render: (value) => (value?.updatedAt ? formatInTimeZone(value?.updatedAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A'),
      key: 'updatedAt',
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (meterData, index) => {
              return (
                <div key={index} className='flex items-center gap-2'>
                  <div
                    className='table-action-btn flex items-center justify-center'
                    onClick={() => {
                      fetchPricingModelData(meterData);
                      setOpen(true);
                      setIsEdit(true);
                    }}
                    style={{ cursor: 'pointer' }}>
                    Update
                  </div>
                  <div
                    className='table-action-btn'
                    onClick={() => {
                      setPricingModelData(meterData);
                      setSubscriptionAction(undefined);
                      setDeletePricingModal(true);
                    }}
                    style={{ cursor: 'pointer' }}>
                    Delete
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const fetchData = async (queryParams) => {
    try {
      const paramsData = { offset: queryParams?.offset, limit: queryParams?.limit };
      if (queryParams?.search !== '') {
        paramsData['search_key'] = queryParams?.search?.trim();
      }
      const formattedData = filtersDataValues(queryParams?.filtersData);
      const params = { ...paramsData, ...formattedData, ...(store?.isSelected && { store_id: store?._id }), xap_request: true };
      const response = await getAllPricingModels(params);
      return response?.data?.data;
    } catch (err) {
      console.log(err);
    }
  };

  const { data, refetch, isLoading, isFetching } = useQuery('pricingModels', () => fetchData(queryParams), {
    enabled: false,
    manual: true,
  });

  useEffect(() => {
    refetch();
  }, [trigerApi, store]);

  useEffect(() => {
    if (!location?.state?.isRedirection) {
      return;
    }

    setSearch(location?.state?.searchValue || '');
    setRecordId(location?.state?.id || '');
    const bypassQueryParams = handleStateByPassData(location?.state?.filtersData, true);
    if (bypassQueryParams) {
      setQueryParams(bypassQueryParams);
    } else {
      refetch();
    }
    navigate(location?.pathname, { replace: true });

    setTimeout(() => {
      if (tableContainerRef?.current && location?.state?.id) {
        const row = document?.querySelector(`.custom-row-class.highlighted-row`);
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 10);
  }, [location?.state, navigate, refetch]);

  const filtersDataValues = (data) => {
    const filtersData = {};
    if (data?.charge_type && data?.charge_type !== 'all') {
      filtersData.charge_type = data?.charge_type;
    }
    if (data?.type && data?.type !== 'all') {
      filtersData.type = data?.type;
    }
    if (data?.combination_operator && data?.combination_operator !== 'all') {
      filtersData.combination_operator = data?.combination_operator;
    }
    if (data?.or_strategy && data?.or_strategy !== 'all') {
      filtersData.or_strategy = data?.or_strategy;
    }
    if (data?.date_range) {
      const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
      const { start_date, end_date } = handleDateRangeFormat(data?.date_range, timezone);
      filtersData.start_date = start_date;
      filtersData.end_date = end_date;
    }
    return filtersData;
  };

  const getSearch = (value) => {
    if (!value) {
      notification?.error({ message: 'Please enter details for search', placement: 'bottomRight' });
    } else {
      setQueryParams((pre) => ({ ...pre, currentPage: 1, offset: 0, search: value }));
      setTrigerApi(!trigerApi);
    }
  };

  const handleSearch = (e) => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    setSearch(e?.target?.value);
  };

  const handleSearchData = () => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      handleRefresh();
    }, 1500);
    setDelayTimer(newTimer);
  };

  const handleRefresh = (isRefresh = true) => {
    if (isRefresh) {
      setSearch('');
    } else {
      setIsFilterModelOpen(false);
    }
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, search: '', filtersData: isRefresh ? pre?.filtersData : null }));
    setTrigerApi(!trigerApi);
  };

  const handleCloseFilterModel = () => {
    setIsFilterModelOpen(!isFilterModelOpen);
  };

  const handleFiltersSearch = (data) => {
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, filtersData: data }));
    setTrigerApi(!trigerApi);
  };

  const filterToggler = () => {
    setFilterToggle(true);
  };

  const handlePagePageChange = (page, pageSize) => {
    if (queryParams?.limit !== pageSize) {
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: 0, currentPage: 1 }));
      setTrigerApi(!trigerApi);
    } else {
      const countOffset = page * queryParams?.limit - queryParams?.limit;
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: countOffset, currentPage: page }));
      setTrigerApi(!trigerApi);
    }
  };

  const handleReset = () => {
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, filtersData: {} }));
    setTrigerApi(!trigerApi);
    setIsFilterModelOpen(false);
  };

  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '700', color: '#262626' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <div className='intentFilters'>
        <Filter filtersData={queryParams?.filtersData} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} />
      </div>
    </div>
  );

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Billing Engine',
      },
      {
        name: 'Pricing Model',
      },
    ],
  };

  const handleRowHover = () => {
    setRecordId('');
  };

  return (
    <div style={{ padding: '0px', width: '100%' }}>
      <div className='header-container'>
        <Breadcrumbs {...breadcrumbProps} />
        <div className='headerPage'>
          <div className='mb-0'>
            <PageHeader label='Pricing Model' button={<ResetButton onClick={isFetching ? '' : handleRefresh} loading={isFetching} />} />
          </div>
          {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
            <div className='flex txn-btn'>
              <div>
                <Button className='primaryBtn mr-0 rounded-lg create_button' onClick={handleCreatePricingModel}>
                  <span className='text-base'>Create Pricing Model</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='actionPanel'>
        <div className='headerContainer' style={{ width: '100%' }}>
          <Popover
            className={isFilterModelOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
            trigger='click'
            placement='bottomLeft'
            content={popOverContent}
            open={isFilterModelOpen}
            destroyTooltipOnHide
            onOpenChange={() => {
              handleCloseFilterModel();
            }}>
            <FilterButton onClick={() => setIsFilterModelOpen(true)}>
              <div className='text-base'>Filter</div>
            </FilterButton>
          </Popover>
          <div className='searchInput' style={{ width: '100%' }}>
            <SearchField
              handleOnChange={handleSearch}
              value={search}
              handlePressEnter={getSearch}
              onReset={handleSearchData}
              placeholder={'Search by Pricing Model ID, Name'}
              loading={!!(isFetching && search)}
              dropdown={{ show: false }}
              enableSearchBtn={true}
            />
          </div>
        </div>
      </div>
      <br />
      <div className='overflow-x-auto custom-table' ref={tableContainerRef}>
        <Table
          loading={isLoading}
          dataSource={data?.pricingModels || []}
          columns={columnsData}
          scroll={{ x: 'auto' }}
          onRow={(record) => {
            return {
              onClick: (event) => {
                const { className } = event?.target;
                if (!className?.includes('table-action-btn')) {
                  navigate(`/billing-engine/pricing-model/${record?.id}`, {
                    state: {
                      record,
                      id: record?.id,
                      filtersData: handleStateByPassData(queryParams),
                      searchValue: search,
                    },
                  });
                }
              },
              onMouseEnter: handleRowHover,
              className: `custom-row-class ${recordId === record?.id ? 'highlighted-row' : ''}`,
            };
          }}
          totalLenght={data?.count || 0}
          pagingation={{
            handlePageChange: handlePagePageChange,
            perPageSize: queryParams?.limit,
            page: queryParams?.currentPage,
          }}
        />
      </div>
      <Modal
        open={deletePricingModal}
        centered
        width={400}
        onCancel={() => {
          setDeletePricingModal(false);
          setSubscriptionAction(undefined);
        }}
        footer={false}
        okButtonProps={{ loading: isPricingModelDeleting }}
        destroyOnClose>
        <header className='font-semibold text-base mb-4'>Delete Pricing Model</header>
        <div style={{ marginBottom: 12 }}>
          <div className='font-semibold mb-2'>Associated Subscription Action</div>
          <Select
            key={deletePricingModal ? `delete-action-${pricingModelData?.id}` : 'delete-action-closed'}
            placeholder='Select action'
            value={subscriptionAction}
            optionsValue={[
              { label: 'Cancel', value: 'cancel' },
              { label: 'Pause', value: 'pause' },
            ]}
            labelInValue={false}
            onChange={(e) => setSubscriptionAction(e?.value ?? e)}
            style={{ width: '100%' }}
          />
        </div>
        <div className='mb-8'>
          Are you sure you want to delete this Pricing Model<span>{'?'}</span>
        </div>
        <div className='flex mt-4 popupConfirmBtn'>
          <Button
            onClick={() => {
              setDeletePricingModal(false);
              setSubscriptionAction(undefined);
            }}
            block
            className='rounded-lg bg-plain'>
            Cancel
          </Button>
          <Button
            className='DeleteBtn ml-4 rounded-lg'
            block
            onClick={() => handleDeletePricingModel(pricingModelData, subscriptionAction)}
            loading={isPricingModelDeleting}
            disabled={!subscriptionAction}>
            Confirm
          </Button>
        </div>
      </Modal>
      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : isEdit ? 450 : 650} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            <span className='createIcon'>
              <DiffOutlined />
            </span>
          </div>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {!isEdit ? 'Add new Pricing Model' : 'Update Pricing Model'}
          </div>
          {dataLoading ? (
            <div style={{ width: '100%' }}>
              <Skeleton active />
            </div>
          ) : (
            <CreatePricingModel handleOpen={handleOpen} refetch={refetch} meterData={pricingModelData} isEdit={isEdit} />
          )}{' '}
        </div>
      </Modal>
    </div>
  );
};
